import * as React from "react";
import {
  User,
  ChevronDown,
  Eye,
  UserSearch,
  Pencil,
  ShieldCheck,
  Search,
  ArrowUpDown,
  AlertCircle,
  Loader2,
  MoreHorizontal,
  X,
  UserCog,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";
import { debounce } from "@/common/utils/debounce";
import { trpc, trpcClient } from "@/trpc";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminUserDetails } from "@/features/user/components/AdminUserDetails";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { Permission, usePermissions } from "@/hooks/usePermissions";
import { ImpersonateUserDialog } from "./_components/impersonate-user-dialog";

const adminRouteApi = getRouteApi("/_admin");

export type AdminSearchUser = {
  id: string;
  email: string | null;
  first_name: string;
  last_name: string;
  created_at: string;
  credential_count: number;
  onboarding_completed: boolean;
  deleted_at: string | null;
  organizations: string[];
  global_permissions: string[];
};

export const Route = createFileRoute("/_admin/admin/users/")({
  component: UsersPage,
});

const editUserSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

const areSamePermissions = (a: Permission[], b: Permission[]) => {
  if (a.length !== b.length) {
    return false;
  }

  const aSet = new Set(a);
  return b.every((permission) => aSet.has(permission));
};

function EditUserDialog({
  user,
  onSuccess,
}: {
  user: AdminSearchUser;
  onSuccess: () => void;
}) {
  const emailInputId = React.useId();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: user.email ?? "",
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (data: { email: string; userId: string }) =>
      trpcClient.user.adminUpdate.mutate(data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const onSubmit = (data: EditUserFormData) => {
    updateUserMutation.mutate({
      email: data.email,
      userId: user.id,
    });
  };

  return (
    <DialogContent className="!w-[calc(100vw-2rem)] !max-w-[425px] !max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="text-xl leading-7">Edit User</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor={emailInputId}>Email</Label>
            <Input
              id={emailInputId}
              aria-invalid={!!errors.email}
              aria-describedby={
                errors.email ? `${emailInputId}-error` : undefined
              }
              {...register("email")}
            />
            {errors.email && (
              <p
                id={`${emailInputId}-error`}
                className="text-sm text-red-700"
                role="alert"
              >
                {errors.email.message}
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="border-t border-slate-200 pt-4">
          <Button type="submit" disabled={updateUserMutation.isPending}>
            {updateUserMutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function UserPermissionsDialog({
  user,
  onClose,
}: {
  user: AdminSearchUser;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selectedPermissions, setSelectedPermissions] = React.useState<
    Permission[]
  >([]);

  const globalPermissionsQuery = useQuery(
    trpc.adminDashboard.getGlobalPermissions.queryOptions(),
  );

  const userGlobalPermissionGrantsQuery = useQuery(
    trpc.adminDashboard.getUserGlobalPermissionGrants.queryOptions({
      userId: user.id,
    }),
  );

  React.useEffect(() => {
    if (userGlobalPermissionGrantsQuery.data) {
      setSelectedPermissions(userGlobalPermissionGrantsQuery.data);
    }
  }, [userGlobalPermissionGrantsQuery.data]);

  const updatePermissionsMutation = useMutation(
    trpc.adminDashboard.updateUserGlobalPermissionGrants.mutationOptions({
      onSuccess: async () => {
        toast.success("Global permissions updated.");
        await queryClient.invalidateQueries({
          queryKey: trpc.adminDashboard.getUsers.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.adminDashboard.getUserGlobalPermissionGrants.queryKey({
            userId: user.id,
          }),
        });
        onClose();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const savedPermissions = userGlobalPermissionGrantsQuery.data ?? [];
  const hasChanges = !areSamePermissions(selectedPermissions, savedPermissions);

  const togglePermission = (permissionId: Permission, checked: boolean) => {
    setSelectedPermissions((current) =>
      checked
        ? Array.from(new Set([...current, permissionId]))
        : current.filter((id) => id !== permissionId),
    );
  };

  const savePermissions = () => {
    updatePermissionsMutation.mutate({
      userId: user.id,
      permissionIds: selectedPermissions,
    });
  };

  return (
    <DialogContent className="!w-[calc(100vw-2rem)] !max-w-[640px] !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
      <DialogHeader className="pr-6 text-left">
        <DialogTitle className="text-xl leading-7">
          Global Permissions
        </DialogTitle>
        <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
          Grant app-wide admin permissions for {user.first_name}{" "}
          {user.last_name} ({user.email}).
        </DialogDescription>
      </DialogHeader>

      {globalPermissionsQuery.isLoading ||
      userGlobalPermissionGrantsQuery.isLoading ? (
        <div
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-600"
          role="status"
        >
          <Loader2
            aria-hidden="true"
            className="size-4 shrink-0 animate-spin text-primary"
          />
          Loading permissions...
        </div>
      ) : globalPermissionsQuery.error ||
        userGlobalPermissionGrantsQuery.error ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          role="alert"
        >
          Failed to load global permissions.
        </div>
      ) : (
        <div className="max-h-[55dvh] min-w-0 overflow-y-auto divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
          {globalPermissionsQuery.data?.map((permission) => (
            <label
              key={permission.id}
              htmlFor={`user-permission-${user.id}-${permission.id}`}
              className="flex min-w-0 cursor-pointer items-start gap-3 p-4 transition-colors hover:bg-slate-50"
            >
              <Checkbox
                id={`user-permission-${user.id}-${permission.id}`}
                checked={selectedPermissions.includes(permission.id)}
                onCheckedChange={(checked) =>
                  togglePermission(permission.id, checked === true)
                }
                disabled={updatePermissionsMutation.isPending}
                className="mt-0.5 shrink-0"
              />
              <span className="min-w-0 space-y-1 [overflow-wrap:anywhere]">
                <span className="block text-sm font-medium text-slate-900">
                  {permission.displayText}
                </span>
                {permission.description ? (
                  <span className="block text-sm leading-6 text-slate-600">
                    {permission.description}
                  </span>
                ) : null}
                <span className="block font-mono text-xs text-slate-500">
                  {permission.id}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      <DialogFooter className="mt-0 border-t border-slate-200 pt-4 sm:flex-row sm:space-x-0">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={updatePermissionsMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={savePermissions}
          disabled={
            !hasChanges ||
            updatePermissionsMutation.isPending ||
            globalPermissionsQuery.isLoading ||
            userGlobalPermissionGrantsQuery.isLoading
          }
        >
          {updatePermissionsMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function UsersPage() {
  const queryClient = useQueryClient();
  const { user: staffUser } = adminRouteApi.useLoaderData();
  const { hasPermission } = usePermissions(staffUser);
  // Details, profile, edit, and permissions call admin-only endpoints.
  const canManageUsers = staffUser?.is_admin === true;
  const canImpersonate = hasPermission(Permission.AppImpersonateUsers);
  const filterId = React.useId();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [hasGlobalPermissionsFilter, setHasGlobalPermissionsFilter] =
    React.useState(false);
  const [editingUser, setEditingUser] = React.useState<AdminSearchUser | null>(
    null,
  );
  const [permissionsUser, setPermissionsUser] =
    React.useState<AdminSearchUser | null>(null);
  const [viewingUser, setViewingUser] = React.useState<AdminSearchUser | null>(
    null,
  );
  const [impersonatingUser, setImpersonatingUser] =
    React.useState<AdminSearchUser | null>(null);
  const [profileModalUserId, setProfileModalUserId] = React.useState<
    string | null
  >(null);
  const [sortConfig, setSortConfig] = React.useState<{
    key: keyof AdminSearchUser | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });

  const usersQuery = useQuery(
    trpc.adminDashboard.getUsers.queryOptions({
      search: searchQuery,
      hasGlobalPermissions: hasGlobalPermissionsFilter || undefined,
    }),
  );

  const sortedUsers = React.useMemo(() => {
    const users = [...(usersQuery.data ?? [])];
    if (!sortConfig.key) {
      return users;
    }

    return users.sort((a, b) => {
      const key = sortConfig.key;
      if (!key || !a[key] || !b[key]) {
        return 0;
      }

      if (a[key] < b[key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [usersQuery.data, sortConfig]);

  const requestSort = (key: keyof AdminSearchUser) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const sortDirection =
    sortConfig.direction === "asc" ? "ascending" : "descending";

  return (
    <div className="min-h-full min-w-0 bg-slate-50">
      <div className="mx-auto max-w-7xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-4 sm:py-8 lg:px-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
            Users
          </h1>
        </div>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs sm:p-5">
          <div className="grid min-w-0 gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="grid min-w-0 gap-2 md:max-w-lg">
              <Label htmlFor={`${filterId}-search`}>Search users</Label>
              <div className="relative min-w-0">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-slate-500"
                />
                <Input
                  id={`${filterId}-search`}
                  placeholder="Search users..."
                  onChange={debounce(
                    (e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchQuery(e.target.value);
                    },
                    500,
                  )}
                  className="min-w-0 pl-10"
                />
              </div>
            </div>
            <label
              htmlFor={`${filterId}-global-permissions`}
              className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            >
              <Checkbox
                id={`${filterId}-global-permissions`}
                checked={hasGlobalPermissionsFilter}
                onCheckedChange={(checked) =>
                  setHasGlobalPermissionsFilter(checked === true)
                }
              />
              Has global permissions
            </label>
          </div>
        </div>

        {usersQuery.isLoading && (
          <div
            className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-xs"
            role="status"
          >
            <div className="rounded-2xl bg-primary/5 p-4">
              <Loader2
                aria-hidden="true"
                className="size-8 animate-spin text-primary"
              />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              Loading users
            </h2>
            <p className="text-sm text-slate-600">
              Please wait while we fetch user accounts.
            </p>
          </div>
        )}

        {usersQuery.isError && (
          <div
            className="flex flex-col items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center"
            role="alert"
          >
            <div className="rounded-2xl bg-red-100 p-4">
              <AlertCircle aria-hidden="true" className="size-8 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              Unable to load users
            </h2>
            <p className="text-sm text-slate-600">Please try again.</p>
            <Button type="button" onClick={() => usersQuery.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {usersQuery.isSuccess && sortedUsers.length > 0 && (
          <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xs">
            <div
              className="max-w-full overflow-x-auto focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              role="region"
              aria-label="Users table"
              tabIndex={0}
            >
              <Table className="min-w-[1100px] text-sm">
                <TableHeader className="bg-slate-50 [&_th]:px-4 [&_th]:text-slate-600">
                  <TableRow className="border-slate-200 hover:bg-slate-50">
                    <TableHead
                      className="min-w-48"
                      aria-sort={
                        sortConfig.key === "first_name"
                          ? sortDirection
                          : undefined
                      }
                    >
                      <Button
                        variant="ghost"
                        className="gap-2 px-0 text-sm text-slate-600 hover:bg-transparent hover:text-primary"
                        onClick={() => requestSort("first_name")}
                      >
                        Name
                        <ArrowUpDown
                          aria-hidden="true"
                          className="size-3.5 text-slate-400"
                        />
                      </Button>
                    </TableHead>
                    <TableHead
                      className="min-w-52"
                      aria-sort={
                        sortConfig.key === "email" ? sortDirection : undefined
                      }
                    >
                      <Button
                        variant="ghost"
                        className="gap-2 px-0 text-sm text-slate-600 hover:bg-transparent hover:text-primary"
                        onClick={() => requestSort("email")}
                      >
                        Email
                        <ArrowUpDown
                          aria-hidden="true"
                          className="size-3.5 text-slate-400"
                        />
                      </Button>
                    </TableHead>
                    <TableHead
                      className="whitespace-nowrap"
                      aria-sort={
                        sortConfig.key === "created_at"
                          ? sortDirection
                          : undefined
                      }
                    >
                      <Button
                        variant="ghost"
                        className="gap-2 px-0 text-sm text-slate-600 hover:bg-transparent hover:text-primary"
                        onClick={() => requestSort("created_at")}
                      >
                        Joined
                        <ArrowUpDown
                          aria-hidden="true"
                          className="size-3.5 text-slate-400"
                        />
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">Credentials</TableHead>
                    <TableHead className="text-center">Onboarding</TableHead>
                    <TableHead className="min-w-40">Organizations</TableHead>
                    <TableHead className="min-w-48">
                      Global Permissions
                    </TableHead>
                    <TableHead className="sticky right-0 z-20 bg-slate-50 text-right shadow-[-1px_0_0_0_var(--color-slate-200)]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_td]:px-4 [&_td]:py-4 [&_td]:text-slate-700">
                  {sortedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="group border-slate-200 hover:bg-slate-50/70"
                    >
                      <TableCell className="max-w-64">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
                            <User aria-hidden="true" className="size-4" />
                          </span>
                          <div className="flex min-w-0 flex-wrap items-center gap-2 font-medium text-slate-900">
                            <span className="[overflow-wrap:anywhere]">
                              {user.first_name} {user.last_name}
                            </span>
                            {user.deleted_at && (
                              <Badge
                                variant="destructive"
                                size="small"
                                className="shrink-0"
                              >
                                Deleted
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-64 [overflow-wrap:anywhere]">
                        {user.email ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-500">
                        {format(new Date(user.created_at), "MM-dd-yy h:mm a")}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {user.credential_count}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          size="small"
                          className={
                            user.onboarding_completed
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-50 text-slate-600"
                          }
                        >
                          {user.onboarding_completed ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-64 [overflow-wrap:anywhere]">
                        {user.organizations.length > 0
                          ? user.organizations.join(", ")
                          : "None"}
                      </TableCell>
                      <TableCell className="max-w-64">
                        {user.global_permissions.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.global_permissions.map((permission) => (
                              <Badge
                                key={permission}
                                variant="outline"
                                size="small"
                                className="max-w-full whitespace-normal border-primary/15 bg-primary/5 text-primary [overflow-wrap:anywhere]"
                              >
                                {permission}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-500">None</span>
                        )}
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-white text-right shadow-[-1px_0_0_0_var(--color-slate-200)] group-hover:bg-slate-50">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-12 gap-2 px-0 sm:w-auto sm:px-5"
                              aria-label={`Actions for ${user.first_name} ${user.last_name}`}
                            >
                              <span className="hidden sm:inline">Actions</span>
                              <MoreHorizontal
                                aria-hidden="true"
                                className="size-5 text-slate-500 sm:hidden"
                              />
                              <ChevronDown
                                aria-hidden="true"
                                className="hidden size-4 shrink-0 text-slate-500 sm:block"
                              />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="min-w-44 rounded-xl border-slate-200 bg-white p-1.5 text-slate-700 shadow-lg"
                          >
                            {canManageUsers && (
                              <>
                                <DropdownMenuItem
                                  className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                  onClick={() => setViewingUser(user)}
                                >
                                  <Eye
                                    aria-hidden="true"
                                    className="h-4 w-4 mr-2 shrink-0"
                                  />
                                  Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                  onClick={() => setProfileModalUserId(user.id)}
                                >
                                  <UserSearch
                                    aria-hidden="true"
                                    className="h-4 w-4 mr-2 shrink-0"
                                  />
                                  View Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                  onClick={() => setEditingUser(user)}
                                >
                                  <Pencil
                                    aria-hidden="true"
                                    className="h-4 w-4 mr-2 shrink-0"
                                  />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                  onClick={() => setPermissionsUser(user)}
                                >
                                  <ShieldCheck
                                    aria-hidden="true"
                                    className="h-4 w-4 mr-2 shrink-0"
                                  />
                                  Permissions
                                </DropdownMenuItem>
                              </>
                            )}
                            {canImpersonate && !user.deleted_at && (
                              <DropdownMenuItem
                                className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                onClick={() => setImpersonatingUser(user)}
                              >
                                <UserCog
                                  aria-hidden="true"
                                  className="h-4 w-4 mr-2 shrink-0"
                                />
                                Impersonate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {usersQuery.isSuccess && sortedUsers.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-xs">
            <div className="rounded-2xl bg-primary/5 p-4">
              <User aria-hidden="true" className="size-8 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              No users found
            </h2>
            <p className="text-sm text-slate-600">
              Try adjusting your search query
            </p>
          </div>
        )}
      </div>
      {editingUser && (
        <Dialog
          open={!!editingUser}
          onOpenChange={() => {
            setEditingUser(null);
          }}
        >
          <EditUserDialog
            user={editingUser}
            onSuccess={async () => {
              await queryClient.invalidateQueries({
                queryKey: trpc.adminDashboard.getUsers.queryKey(),
              });
              setEditingUser(null);
            }}
          />
        </Dialog>
      )}
      {permissionsUser && (
        <Dialog
          open={!!permissionsUser}
          onOpenChange={() => {
            setPermissionsUser(null);
          }}
        >
          <UserPermissionsDialog
            user={permissionsUser}
            onClose={() => setPermissionsUser(null)}
          />
        </Dialog>
      )}
      {impersonatingUser && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setImpersonatingUser(null);
            }
          }}
        >
          <ImpersonateUserDialog
            user={impersonatingUser}
            onCancel={() => setImpersonatingUser(null)}
          />
        </Dialog>
      )}
      {profileModalUserId && (
        <ProfileModal
          userId={profileModalUserId}
          onClose={() => setProfileModalUserId(null)}
          showChatButton={false}
        />
      )}
      {viewingUser && (
        <Dialog
          open={!!viewingUser}
          onOpenChange={() => {
            setViewingUser(null);
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="!w-[calc(100vw-2rem)] !max-w-6xl !max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-2xl border-slate-200 bg-slate-50 p-0 text-slate-900 sm:rounded-2xl"
          >
            <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b border-slate-200 bg-white px-4 py-3 text-left sm:px-6">
              <DialogTitle className="text-xl">User Details</DialogTitle>
              <DialogDescription className="sr-only">
                Account information, communication history, and job activity.
              </DialogDescription>
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-12 shrink-0 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                >
                  <X aria-hidden="true" className="size-5" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogClose>
            </DialogHeader>
            <AdminUserDetails
              userId={viewingUser.id}
              context="dialog"
              onClose={() => setViewingUser(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
